import React from 'react';

// A Reply Engine guide, shown formatted. Guides are plain text (written by the
// study, editable by hand) read line by line; a line that fits no pattern shows
// as a paragraph, so a hand-edited guide still reads.

const LINE_KINDS = [
  ['title', /^# (.+)$/],
  ['heading', /^## (.+)$/],
  ['share', /^\d+\.\s+(.+?)\s+\((\d+)%\):\s*(.*)$/],                     // 1. Instant Zinger (26%): …
  ['question', /^Q: (.+)$/],                                             // a reply guide example…
  ['answer', /^A \((\d+) points, (.+?)\): (.+)$/],                       // …and its top answer
  ['topQuestion', /^- "(.+)" \((\d+) upvotes, (\d+) comments; (.+)\)$/], // a question guide example
  ['bullet', /^- (.+)$/],
  ['text', /^(.+)$/]
];
const PART_LABELS = /\s+(Wins on|Length|Why it works):\s*/; // inside a style or a question type

const TEXT = { fontSize: '14px', lineHeight: 1.6, color: '#dddddd' };
const BOX = { background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px' };

function lineOf(raw) {
  const text = raw.trim();
  for (const [kind, re] of LINE_KINDS) {
    const m = re.exec(text);
    if (m) return { kind, m };
  }
  return null;
}

// Lines -> blocks: bullets in a row make one list; an answer joins its question.
function blocksOf(guide) {
  const blocks = [];
  for (const line of String(guide || '').split('\n').map(lineOf).filter(Boolean)) {
    const last = blocks[blocks.length - 1];
    if (line.kind === 'answer' && last && last.kind === 'question' && !last.answer) last.answer = line.m;
    else if (line.kind === 'bullet' && last && last.kind === 'bullet') last.items.push(line.m[1]);
    else if (line.kind === 'bullet') blocks.push({ kind: 'bullet', items: [line.m[1]] });
    else blocks.push(line);
  }
  return blocks;
}

// "Title only: 0% of top questions…": the label in bold.
function Labelled({ text }) {
  const m = /^([\w/ -]{2,40}): (.+)$/.exec(text);
  return m ? <><b>{m[1]}:</b> {m[2]}</> : text;
}

// "desc Wins on: x Length: y" -> ['desc', [['Wins on', 'x'], ['Length', 'y']]]
function partsOf(text) {
  const [description, ...rest] = text.split(PART_LABELS);
  const parts = [];
  for (let i = 0; i < rest.length; i += 2) parts.push([rest[i], rest[i + 1]]);
  return [description, parts];
}

function Share({ m }) {
  const [, name, pct, rest] = m;
  const [description, parts] = partsOf(rest);
  return (
    <div style={BOX}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <strong style={{ fontSize: '14px' }}>{name}</strong>
        <span style={{ width: '120px', height: '6px', background: '#2a2a2a', borderRadius: '3px' }}>
          <span style={{ display: 'block', width: `${Math.min(Number(pct), 100)}%`, height: '100%', background: '#ff4500', borderRadius: '3px' }} />
        </span>
        <span className="sub">{pct}%</span>
      </div>
      {description && <p style={{ ...TEXT, marginTop: '6px' }}>{description}</p>}
      {parts.map(([label, value]) => <p key={label} className="sub" style={{ marginTop: '4px', lineHeight: 1.5 }}><b>{label}:</b> {value}</p>)}
    </div>
  );
}

function Example({ question, answer }) {
  return (
    <div style={BOX}>
      {question && <p className="sub" style={{ fontSize: '13px', lineHeight: 1.5 }}>{question}</p>}
      {answer && (
        <p style={{ ...TEXT, marginTop: question ? '6px' : 0 }}>
          {answer[3]}
          <span className="sub" style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}><span className="tag">{answer[2]}</span> {Number(answer[1]).toLocaleString()} points</span>
        </p>
      )}
    </div>
  );
}

function TopQuestion({ m }) {
  const [, title, upvotes, comments, type] = m;
  return (
    <div style={BOX}>
      <p style={TEXT}>{title}</p>
      <p className="sub" style={{ marginTop: '4px' }}>
        <span className="tag">{type}</span> {Number(upvotes).toLocaleString()} upvotes · {Number(comments).toLocaleString()} comments
      </p>
    </div>
  );
}

function Block({ block }) {
  const { kind, m } = block;
  if (kind === 'title') return <h3 style={{ fontSize: '17px', marginBottom: '4px' }}>{m[1]}</h3>;
  if (kind === 'heading') return <h4 style={{ fontSize: '14px', color: '#ff4500', margin: '20px 0 8px' }}>{m[1]}</h4>;
  if (kind === 'share') return <Share m={m} />;
  if (kind === 'question') return <Example question={m[1]} answer={block.answer} />;
  if (kind === 'answer') return <Example answer={m} />;
  if (kind === 'topQuestion') return <TopQuestion m={m} />;
  if (kind === 'bullet') {
    return <ul style={{ paddingLeft: '20px', margin: '4px 0' }}>{block.items.map((item, i) => <li key={i} style={{ ...TEXT, marginBottom: '4px' }}><Labelled text={item} /></li>)}</ul>;
  }
  return <p style={{ ...TEXT, marginBottom: '6px' }}>{m[1]}</p>;
}

function GuideView({ text }) {
  return <div style={{ maxWidth: '860px' }}>{blocksOf(text).map((block, i) => <Block key={i} block={block} />)}</div>;
}

export default GuideView;
